import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AppBar, Modal, Toolbar,  Badge, Box, Divider } from '@mui/material';
import logo from '../assets/ls_logo_no_prospector.png';
import NavDrawer from './NavDrawer';
import StoreSelector from './StoreSelector';
import StoreContext from '../context/StoreContext';
import UserContext from '../context/UserContext';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import HelpMenu from '../components/HelpMenu';
import Typography from '@mui/material/Typography';
import SmartPTTable from './SmartPTTable';
import SmartPTDialog from './SmartPTDialog';
import WorkOrderContext from '../context/WorkOrderContext';
import { getWOsByStatus, workOrdersByDay } from '../helpers/WorkOrders';
import { 
  FIRST_WAITING_TIME_PERIOD,
  SECOND_WAITING_TIME_PERIOD,
  FIRST_DROPOFF_TIME_PERIOD,
  SECOND_DROPOFF_TIME_PERIOD 
} from '../utils/SPTInitialTimes';
import { DateTime } from 'luxon';

const MR_STAGE = process.env.REACT_APP_MR_STAGE;

const Nav = () => {
  const { storeId, label, smartPromiseTime, time_zone } = useContext(StoreContext);
  const { isAdmin } = useContext(UserContext);
  const { workOrders } = useContext(WorkOrderContext);
  const [isOpen, setIsOpen] = useState(false);
  const [storeButtonText, setStoreButtonText] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const helpOpen = Boolean(anchorEl);
  const [isFirstTimePeriod, setIsFirstTimePeriod] = useState(true);
  const [isSmartDialogOpen, setIsSmartDialogOpen] = useState(false);

  const handleClickHelp = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseHelp = () => {
    setAnchorEl(null);
  };
  
  const toggleSmartDialog = () => {
    setIsSmartDialogOpen(!isSmartDialogOpen);
  }

  /*
  POS Monitoring Code
  const [monitoringModalOpen, setMonitoringModalOpen] = useState(false);
  const toggleMonitoringService = () => {
    setMonitoringModalOpen(!monitoringModalOpen);
  };
  */

  useEffect(() => {
    if (+storeId !== 1) {
      setStoreButtonText(label);
    }
  }, [storeId, label])

  useEffect(() => {
    const checkTime = () => {
      const now = DateTime.local();
      if (now.hour >= 15) {
        setIsFirstTimePeriod(false);
      }
    };

    // Check the time immediately when the component mounts
    checkTime();

    // Set up an interval to check the time every minute
    const intervalId = setInterval(checkTime, 60000);
    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);
  
  const toggleSelector = () => {
    setIsOpen(!isOpen);
  };

  const initialSuggestedTime = useMemo(() => {
    let waiting;
    let dropOff;

    if(isFirstTimePeriod) {
      waiting = FIRST_WAITING_TIME_PERIOD;
      dropOff = FIRST_DROPOFF_TIME_PERIOD;
    } else {
      waiting = SECOND_WAITING_TIME_PERIOD;
      dropOff = SECOND_DROPOFF_TIME_PERIOD;
    }

    return {
      waiting,
      dropOff
    };
  }, [isFirstTimePeriod])

  const vehiclesInQueue = useMemo(() => {
    const totalOfVehicles = getWOsByStatus(workOrdersByDay(workOrders, time_zone, 'today'));
    return totalOfVehicles
  }, [workOrders]);

  return (
    <div>
      <AppBar
        position='static'
        className='nav-app-bar'
        id='nav-bar'
        elevation={0}
      >
        <Toolbar variant='regular' className={'nav-full-nav ' + (smartPromiseTime ? 'h-[165px]' : 'h-[100px]')}>
          <div className='nav-left-nav'>
            <NavDrawer/>
            <div className='nav-logo-container'>
              { 
                (MR_STAGE === 'dev' || MR_STAGE === 'preprod' || MR_STAGE === 'qa' || MR_STAGE === 'project-dev') 
                  ? 
                  <Badge badgeContent={MR_STAGE} color='secondary' className='nav-span-text'>
                    <Badge badgeContent={localStorage.getItem("isInStore") ? "StoreAuth" : "OKTA"} color='primary' className='nav-span-text'>
                    </Badge>
                    <img className='nav-logo' src={logo} alt='ls-logo' />
                  </Badge>
                  : <img className='nav-logo' src={logo} alt='ls-logo' /> 
              }
            </div>
          </div>

          {/* Suggested Promise Time */}
          {smartPromiseTime && 
            <Box className='border border-solid border-gray-300 rounded pb-3 mt-4'>

              <Divider className='border-yellow border-4'/>

              <Typography variant='h6' className='flex justify-center items-center mt-1 capitalize font-bold text-black'>
                Suggested promise time
                <InfoIcon
                  className='table-icon-info ml-1'
                  onClick={toggleSmartDialog}
                />
              </Typography>
              <p className='text-xs text-center text-neutral-500 mb-2 mt-0'>Vehicles in queue: 
                <Badge badgeContent={vehiclesInQueue} color='success' className='nav-span-text ml-3' showZero/>
              </p>
              <Box className='flex justify-center px-3'>
                <SmartPTTable
                  label='Waiting'
                  initialSuggestedTime={initialSuggestedTime?.waiting}
                  customersInQueue={vehiclesInQueue}
                  amountOfServices={5}
                />
                <Box className='mx-2'/>
                <SmartPTTable
                  label='Drop off'
                  initialSuggestedTime={initialSuggestedTime?.dropOff}
                  customersInQueue={vehiclesInQueue}
                  amountOfServices={5}
                />
              </Box>
            </Box>
          }

          <div className='mt-8'>
            <button className='button nav-store-selector-button' onClick={ isAdmin ? toggleSelector : null }>
              {!storeButtonText ? 'Select Store' : storeButtonText}
            </button>
            <div className='flex justify-end items-center mr-8 mt-1'>
              <Typography sx={{fontWeight: 700, fontSize: '16px !important', color: '#000 !important'}}>Help</Typography>
              <IconButton 
                className='nav-help-button'
                id="help-button"
                aria-haspopup="true"
                aria-controls={helpOpen ? 'help-menu' : undefined}
                aria-expanded={helpOpen ? 'true' : undefined}
                onClick={handleClickHelp}
              >
                <HelpIcon/>
              </IconButton>
              <HelpMenu open={helpOpen} handleClose={handleCloseHelp} anchorEl={anchorEl}/>
            </div>
          </div> 

        </Toolbar>
      </AppBar>

      <Modal open={isOpen} onClose={toggleSelector} disableAutoFocus>
        <div className='nav-modal-col'>
          <StoreSelector
            closeModal={toggleSelector}
          />
        </div>
      </Modal>

      { isSmartDialogOpen && 
        <SmartPTDialog 
          initialSuggestedTime={initialSuggestedTime}
          open={isSmartDialogOpen} 
          onClose={toggleSmartDialog}
          timePeriod={isFirstTimePeriod ? '6AM-2PM' : '3PM-5PM'}
        />
      }

    </div>
  );
};

export default Nav;
